import { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { AdditionalDocumentDTO } from "../types/AdditionalDocumentDTO";
import { useAdditionalDocuments } from "../hooks/useAdditionalDocuments";
import { useAdditionalDocumentPreview } from "../hooks/useAdditionalDocumentPreview";
import AdditionalDocumentsGrid from "../components/additional-documents/AdditionalDocumentsGrid";
import AdditionalDocumentPreviewModal from "../components/additional-documents/AdditionalDocumentPreviewModal";
import AddAdditionalDocumentModal from "../components/additional-documents/AddAdditionalDocumentModal";
import { fileToBase64 } from "../utils/base64converter";
import {
  deleteAdditionalDocument,
  uploadAdditionalDocument,
  getAdditionalDocumentFile,
} from "../api/additionalDocuments";
import ConfirmDeleteDialog from "../components/common/ConfirmDeleteDialog";
import { useAppTranslation } from "../hooks/useAppTranslation";
import { buildGridStateStorageKey } from "../utils/gridStatePersistence";

type Props = {
  companyId: string;
};

export default function AdditionalDocumentsPage({ companyId }: Props) {
  const [selectedDocument, setSelectedDocument] =
    useState<AdditionalDocumentDTO | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] =
    useState<AdditionalDocumentDTO | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    documents,
    setDocuments,
    categories,
    loading,
    error,
    reload,
  } = useAdditionalDocuments(companyId);

  const { t } = useAppTranslation("common");

  const gridStorageKey = useMemo(
    () =>
      buildGridStateStorageKey({
        table: "additional-documents",
        companyId,
      }),
    [companyId]
  );

  const {
    fileBase64,
    loading: fileLoading,
    error: fileError,
  } = useAdditionalDocumentPreview(companyId, selectedDocument);

  const handleDownload = (doc: AdditionalDocumentDTO) => {
    if (!fileBase64 || selectedDocument?.SystemId !== doc.SystemId) {
      getAdditionalDocumentFile(companyId, doc.SystemId).then((base64) => {
        const link = document.createElement("a");
        link.href = `data:application/octet-stream;base64,${base64}`;
        link.download = doc.fileName;
        link.click();
      });
      return;
    }

    const link = document.createElement("a");
    link.href = `data:application/octet-stream;base64,${fileBase64}`;
    link.download = doc.fileName;
    link.click();
  };

  const handleDelete = (doc: AdditionalDocumentDTO) => {
    setDeleteCandidate(doc);
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;

    await deleteAdditionalDocument(
      companyId,
      deleteCandidate.SystemId
    );

    setDocuments((prev) =>
      prev.filter((d) => d.SystemId !== deleteCandidate.SystemId)
    );

    setDeleteCandidate(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">
          {t("Błąd ładowania danych")}: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0, p: 2 }}>
      <AdditionalDocumentsGrid
        gridStorageKey={gridStorageKey}
        documents={documents}
        onSelect={(doc) => {
          setSelectedDocument(doc);
          setPreviewOpen(true);
        }}
        onDownload={handleDownload}
        onDelete={handleDelete}
        onAdd={() => {
          setUploadError(null);
          setAddOpen(true);
        }}
      />

      <AdditionalDocumentPreviewModal
        open={previewOpen}
        document={selectedDocument}
        fileBase64={fileBase64}
        loading={fileLoading}
        error={fileError}
        onClose={() => setPreviewOpen(false)}
        onDownload={() => {
          if (!fileBase64 || !selectedDocument) return;

          const link = document.createElement("a");
          link.href = `data:application/octet-stream;base64,${fileBase64}`;
          link.download = selectedDocument.fileName;
          link.click();
        }}
      />

      <ConfirmDeleteDialog
        open={!!deleteCandidate}
        description={
          deleteCandidate
            ? t("Czy na pewno chcesz usunąć dokument '{{fileName}}'?", {
                fileName: deleteCandidate.fileName,
              })
            : ""
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteCandidate(null)}
      />

      <AddAdditionalDocumentModal
        open={addOpen}
        categories={categories}
        uploading={uploading}
        error={uploadError}
        onClose={() => {
          if (!uploading) {
            setAddOpen(false);
            setUploadError(null);
          }
        }}
        onSubmit={async ({
          file,
          description,
          categoryCode,
          accountingCategory,
        }) => {
          try {
            setUploading(true);
            setUploadError(null);

            const base64 = await fileToBase64(file);

            await uploadAdditionalDocument(companyId, {
              fileName: file.name,
              description,
              categoryCode,
              accountingCategory,
              fileBase64: base64,
            });

            await reload();
            setAddOpen(false);
          } catch (err: any) {
            setUploadError(
              err?.message ?? t("Nie udało się dodać dokumentu")
            );
          } finally {
            setUploading(false);
          }
        }}
      />
    </Stack>
  );
}